import { clsx } from "clsx";
import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { type IStringAnyMap } from "../../app-utils/type.js";
import { type Attribute, type InputTableQuerySpec } from "../../graphql.js";
import { loadAttribute, loadAttributesForDecoration } from "../../redux/modules/sample-admin.js";
import {
  type ITableData,
  type ITableInitializationOptions,
  createUniqueKeyFromTableSpec,
  ensureRowIsVisible,
  getTableDataSlice,
  reloadTableRows,
  sortByTableColumn,
} from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import DataTableContainer from "../core/grid/DataTableContainer.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import ModifySampleAttributeDialog from "./ModifySampleAttributeDialog.js";
import SampleAttributesAdminToolbar from "./SampleAttributesAdminToolbar.js";
import { ATTRIBUTE_TABLE_COLUMNS, ENTITY_TYPE } from "@/settings/settings.js";

export const sampleAttributesTableSpec: InputTableQuerySpec = {
  resultType: ENTITY_TYPE.SAMPLE_ATTRIBUTE,
};

interface ISampleAttributesAdminPageOwnProps {}

interface ISampleAttributesAdminPageAppStateProps {
  // Redux injected props
  filteredRowCount: number;
  totalRowCount: number;
}

interface ISampleAttributesAdminPageDispatchProps {
  sortByTableColumn: (columnField: string, sortOrder: string) => void;
  reloadTableRows: (tableSpec: InputTableQuerySpec) => Promise<any>;
  loadAttribute: (attribute: Attribute) => Promise<Attribute>;
  ensureRowIdIsVisible: (rowId: number) => void;
  loadAttributesForDecoration: () => Promise<Attribute[]>;
}

export interface ISampleAttributesAdminPageProps
  extends ISampleAttributesAdminPageAppStateProps,
    ISampleAttributesAdminPageDispatchProps,
    ISampleAttributesAdminPageOwnProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: ISampleAttributesAdminPageOwnProps,
): ISampleAttributesAdminPageAppStateProps {
  const tableData: ITableData = getTableDataSlice(state.tableData, sampleAttributesTableSpec);

  return {
    filteredRowCount: tableData && tableData.filteredRowCount,
    totalRowCount: tableData && tableData.totalRowCount,
  };
}

function mapDispatchToProps(
  dispatch,
  _ownProps: ISampleAttributesAdminPageOwnProps,
): ISampleAttributesAdminPageDispatchProps {
  return {
    sortByTableColumn: (columnField: string, sortOrder: string) =>
      dispatch(sortByTableColumn(columnField, sortOrder, false, sampleAttributesTableSpec)),
    reloadTableRows: (tableSpec: InputTableQuerySpec) => dispatch(reloadTableRows(tableSpec, true)),
    loadAttribute: (attribute: Attribute) => dispatch(loadAttribute(attribute)),
    ensureRowIdIsVisible: (rowId: number) => dispatch(ensureRowIsVisible(sampleAttributesTableSpec, rowId)),
    loadAttributesForDecoration: () => dispatch(loadAttributesForDecoration()),
  };
}

interface ISampleAttributesAdminPageState {
  showEditAttributeDialog?: boolean;
  attributeToEdit?: Attribute;
  attributesForDecoration?: Attribute[]; // meant for resolving decorations for scripts
}

class SampleAttributesAdminPage extends PureComponent<
  ISampleAttributesAdminPageProps,
  ISampleAttributesAdminPageState
> {
  constructor(props: ISampleAttributesAdminPageProps) {
    super(props);

    this.state = {
      showEditAttributeDialog: false,
      attributeToEdit: null,
    };
  }

  getTableInitializationOptions = (): ITableInitializationOptions => {
    return {
      sortExpression: ATTRIBUTE_TABLE_COLUMNS.NAME,
    };
  };

  openEditAttribute = (attribute: Attribute) => {
    this.setState({
      showEditAttributeDialog: true,
      attributeToEdit: attribute,
    });
  };

  private cellRendererParamsLookup: IStringAnyMap = {
    [ATTRIBUTE_TABLE_COLUMNS.NAME]: { openEditAttribute: this.openEditAttribute },
  };

  componentDidMount() {
    this.props
      .loadAttributesForDecoration()
      .then((attributes) => this.setState({ attributesForDecoration: attributes }));
  }

  getCount(): string {
    if (this.props.totalRowCount) {
      if (this.props.filteredRowCount === this.props.totalRowCount) {
        return String(this.props.totalRowCount);
      } else if (this.props.filteredRowCount < this.props.totalRowCount) {
        return `${this.props.filteredRowCount} of ${this.props.totalRowCount}`;
      }
    }
  }

  attributeCreated = (createdAttribute: Attribute) => {
    this.props.ensureRowIdIsVisible(createdAttribute.id);
  };

  render(): ReactElement {
    return (
      <div className={clsx(styleLayout.page, styleLayout.page_scrollable)} style={{ backgroundColor: "white" }}>
        <PageHeader
          title={"Sample Attributes"}
          badge={this.getCount()}
          actionWidgets={[
            <SampleAttributesAdminToolbar
              tableSpec={sampleAttributesTableSpec}
              attributeCreated={this.attributeCreated}
              reloadAttributeRows={() => this.props.reloadTableRows(sampleAttributesTableSpec)}
              key={"sample-attributes-admin-page-action-1"}
            />,
          ]}
        />
        <div style={{ height: "calc(100% - 50px)", overflow: "auto" }}>
          <DataTableContainer
            key={createUniqueKeyFromTableSpec(sampleAttributesTableSpec)}
            tableSpec={sampleAttributesTableSpec}
            tableInitializationOptions={this.getTableInitializationOptions()}
            cellRendererParamsLookup={this.cellRendererParamsLookup}
            gridContext={{ attributesForDecoration: this.state.attributesForDecoration }}
          />
        </div>

        {this.state.showEditAttributeDialog && (
          <ModifySampleAttributeDialog
            attributeToEdit={this.state.attributeToEdit}
            fetchAttribute={this.props.loadAttribute}
            onClose={() => this.setState({ showEditAttributeDialog: false })}
          />
        )}
      </div>
    );
  }
}

export default connect<
  ISampleAttributesAdminPageAppStateProps,
  ISampleAttributesAdminPageDispatchProps,
  ISampleAttributesAdminPageOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(SampleAttributesAdminPage);
